/**
 * Regex pattern builders for date recognition
 */

// Cache compiled regex sets keyed by monthRxStr — `MONTH_NAMES` is static, so in
// practice this is a single-entry cache that survives every keystroke.
const _patternCache = new Map();

/**
 * Build all regex patterns for date recognition
 * @param {string} monthRxStr - Month names regex string
 * @returns {Object} Object with all regex patterns
 */
export function buildDatePatterns(monthRxStr) {
    const cached = _patternCache.get(monthRxStr);
    if (cached) return cached;
    // Flexible separators (spaces, dashes, dots, commas, slashes) - optional (supports compact format without separators)
    const separators = '[\\s\\-\\.,/]*';
    
    // Pattern 1: Day Month Year (e.g., "15 nov 1972", "15 nov 72")
    const dayMonthYearRx = new RegExp(`(?<![\\p{L}\\p{N}])(\\d{1,2})${separators}(${monthRxStr})(?:${separators}(\\d{2}|\\d{4}))?(?=\\s|$|[^\\p{L}\\p{N}])`, 'iu');
    
    // Pattern 2: Month Day Year (e.g., "nov 15 1972", "november 15 72")
    const monthDayYearRx = new RegExp(`(?<![\\p{L}\\p{N}])(${monthRxStr})${separators}(\\d{1,2})(?:${separators}(\\d{2}|\\d{4}))?(?=\\s|$|[^\\p{L}\\p{N}])`, 'iu');
    
    // Pattern 2b: Year Month Day (text month) (e.g., "74 nov 15", "1972 nov 15", "74nov15")
    // Note: separators are optional, so "74nov15" matches without separators
    const yearMonthDayTextRx = new RegExp(`(?<![\\p{L}\\p{N}])(\\d{2}|\\d{4})${separators}(${monthRxStr})${separators}(\\d{1,2})(?=\\s|$|[^\\p{L}\\p{N}])`, 'iu');
    
    // Pattern 3: Numeric dates (e.g., "15.11.1972", "15-11-72", "15/11/1972", "15 11 1972", "151174")
    // Supports same separator throughout, mixed separators, or no separators (compact format)
    const numericDateRx = new RegExp(`(?<![\\p{L}\\p{N}])(\\d{1,2})${separators}(\\d{1,2})(?:${separators}(\\d{2}|\\d{4}))?(?=\\s|$|[^\\p{L}\\p{N}])`, 'iu');
    
    // Pattern 4: ISO format (e.g., "1972-11-15") - requires dashes
    const isoDateRx = new RegExp(`(?<![\\p{L}\\p{N}])(\\d{4})-(\\d{1,2})-(\\d{1,2})(?=\\s|$|[^\\p{L}\\p{N}])`, 'iu');
    
    // Pattern 5: Year Month Day (e.g., "1972 11 15", "1972.11.15", "19721115")
    const yearMonthDayRx = new RegExp(`(?<![\\p{L}\\p{N}])(\\d{4})${separators}(\\d{1,2})${separators}(\\d{1,2})(?=\\s|$|[^\\p{L}\\p{N}])`, 'iu');
    
    // Pattern 6: Month Year (text month) - e.g., "ноя 72", "november 1972"
    const monthYearTextRx = new RegExp(`(?<![\\p{L}\\p{N}])(${monthRxStr})${separators}(\\d{2}|\\d{4})(?=\\s|$|[^\\p{L}\\p{N}])`, 'iu');
    
    // Pattern 7: Year Month (text month) - e.g., "72 ноя", "1972 november"
    const yearMonthTextRx = new RegExp(`(?<![\\p{L}\\p{N}])(\\d{2}|\\d{4})${separators}(${monthRxStr})(?=\\s|$|[^\\p{L}\\p{N}])`, 'iu');
    
    // Pattern 8: Month Year (numeric) - e.g., "11 72", "11/72", "72/11", "72-11", "11.72"
    const monthYearNumericRx = new RegExp(`(?<![\\p{L}\\p{N}])(\\d{1,2})${separators}(\\d{2}|\\d{4})(?=\\s|$|[^\\p{L}\\p{N}])`, 'iu');
    
    // Pattern 9: YYMM[A-Z]DD format (e.g., "5311C18", "7411M15") - filename format with letter separator
    // CRITICAL: Match at start of query OR after non-alphanumeric (like 6-digit compact pattern)
    const yearMonthLetterDayRx = new RegExp(`(?:^|(?<![\\p{L}\\p{N}]))(\\d{2})(\\d{2})([A-Za-z])(\\d{2})(?=\\s|$|[^\\p{L}\\p{N}])`, 'iu');
    
    const patterns = {
        dayMonthYearRx,
        monthDayYearRx,
        yearMonthDayTextRx,
        numericDateRx,
        isoDateRx,
        yearMonthDayRx,
        monthYearTextRx,
        yearMonthTextRx,
        monthYearNumericRx,
        yearMonthLetterDayRx
    };
    _patternCache.set(monthRxStr, patterns);
    return patterns;
}

